import React__default, { useEffect, useCallback } from 'react';
import useCountDown from 'react-countdown-hook';
import { useFormContext } from 'react-hook-form';
import useLocalStorageState from 'use-local-storage-state';
import { Text, Link } from '@sravni/react-design-system/lib/Typography';
import { useStyles } from '@sravni/react-utils';
import { LockedError, TooManyRequestsError, BadRequestError } from '@sravni/utils/lib/errors/network';
import { DEFAULT_COOL_DOWN_TIME, LOCAL_STORAGE_LAST_SEND_OTP_TIME, DEFAULT_ERROR_MESSAGE } from '../../constansts/index.js';
import { convertMillisecondsToHumanString } from '../../helpers/time.js';
import { joinRetryAfterAndErrorType, getWindowLocation } from '../../helpers/utils.js';
import '../ContextProvider/components/IsLoadingProvider/provider.js';
import { useIsLoading } from '../ContextProvider/components/IsLoadingProvider/hook.js';
import { useSendOtpByPhoneNumber } from '../../hooks/useSendOtpByPhoneNumber.js';
import modules_13b53a7e from './styles.module.less.js';

/** Компонент в виде ссылки для отправки One Time Code */
const GetOTPLink = React__default.forwardRef((props, ref) => {
    const { size = 12, className, onClick: onClickOutside, inputName = "code" /* FIELD_NAME.OTP */, ...rest } = props;
    const cx = useStyles(modules_13b53a7e);
    const { watch, setError, clearErrors } = useFormContext();
    const returnUrl = getWindowLocation();
    const [isLoading, setIsLoading] = useIsLoading();
    const [coolDownTime, { start: startCoolDownTimer }] = useCountDown(DEFAULT_COOL_DOWN_TIME, 1000);
    const sendOtpByPhoneNumber = useSendOtpByPhoneNumber();
    const [lastSendOtpTime] = useLocalStorageState(LOCAL_STORAGE_LAST_SEND_OTP_TIME, {
        storageSync: true,
        ssr: true,
    });
    const phone = watch("phone" /* FIELD_NAME.PHONE */);
    const name = watch("name" /* FIELD_NAME.NAME */);
    useEffect(() => {
        if (typeof lastSendOtpTime !== 'number')
            return;
        const sendCodeTimeDiff = Math.round((DEFAULT_COOL_DOWN_TIME - (Date.now() - lastSendOtpTime)) / 1000) * 1000;
        if (sendCodeTimeDiff < 0)
            return;
        startCoolDownTimer(sendCodeTimeDiff);
    }, [lastSendOtpTime, startCoolDownTimer]);
    const handleGetCodeLinkClick = useCallback(async (event) => {
        if (isLoading)
            return;
        onClickOutside?.(event);
        clearErrors(inputName);
        setIsLoading(true);
        const result = await sendOtpByPhoneNumber({ phone, name, returnUrl });
        setIsLoading(false);
        if (result.isErr()) {
            if (result.error instanceof LockedError) {
                setError("root.server" /* FIELD_NAME.SERVER */, { message: 'Данный аккаунт был заблокирован, попробуйте войти позже.' });
                return;
            }
            if (result.error instanceof TooManyRequestsError) {
                const { retryAfter } = result.error;
                if (!retryAfter) {
                    setError(inputName, { message: `Слишком много попыток, попробуйте позднее` });
                    return;
                }
                setError(inputName, {
                    type: joinRetryAfterAndErrorType(retryAfter),
                    message: `Слишком много попыток, попробуйте снова через`,
                });
                return;
            }
            if (result.error instanceof BadRequestError) {
                const { target = "root.server" /* FIELD_NAME.SERVER */ } = result.error;
                setError(target, { message: result.error.message || DEFAULT_ERROR_MESSAGE });
                return;
            }
            setError("root.server" /* FIELD_NAME.SERVER */, { message: DEFAULT_ERROR_MESSAGE });
        }
    }, [
        clearErrors,
        inputName,
        isLoading,
        name,
        onClickOutside,
        phone,
        returnUrl,
        sendOtpByPhoneNumber,
        setError,
        setIsLoading,
    ]);
    if (coolDownTime > 0)
        return (React__default.createElement(Text, { size: size, className: cx('secondary', className), ...rest, ref: ref },
            "\u041E\u0442\u043F\u0440\u0430\u0432\u0438\u0442\u044C \u043A\u043E\u0434 \u043F\u043E\u0432\u0442\u043E\u0440\u043D\u043E ",
            convertMillisecondsToHumanString(coolDownTime)));
    return (React__default.createElement(Link, { onClick: handleGetCodeLinkClick, className: className, ...rest, ref: ref },
        React__default.createElement(Text, { size: size }, "\u041F\u043E\u043B\u0443\u0447\u0438\u0442\u044C \u043D\u043E\u0432\u044B\u0439 \u043A\u043E\u0434")));
});

export { GetOTPLink };
//# sourceMappingURL=view.js.map
